import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ComponentVariantEnum } from '~/component/enums/variant';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { TitleService } from '~/core/services/title/title.service';
import { OrganizerSharedPageAbstract } from '~/organizer/shared/components/pages/abstract/page.abstract';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

import { OrganizerSharedPageOverviewPageInterface } from './interfaces/page.interface';

@Component({
  selector: 'app-organizer-shared-page-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
})
export class OrganizerSharedPageOverviewComponent
  extends OrganizerSharedPageAbstract<OrganizerSharedPageOverviewPageInterface>
  implements OnDestroy, OnInit
{
  private readonly subscriptions = new Subscription();

  protected readonly pageCssClass = 'organizer-shared-page-overview';

  public activeStep: number;
  public descriptionKey: string;
  public hasCloseButton = true;
  public hasPreviousButton = true;
  public imageSource: string;
  public isMobileDevice: boolean;
  public mobileImageSource?: string;
  public nextLabel = '';
  public nextLabelKey?: string;
  public stepsNumber: number;
  public titleStepperKey: string;

  constructor(
    private readonly translateService: TranslateService,
    private readonly matchMediaService: MatchMediaService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly location: Location,
    protected readonly titleService: TitleService,
    private readonly paramethorService: ParamethorService
  ) {
    super(activatedRoute, location, titleService);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.buildNextLabel();

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );
  }

  public get backgroundStyle(): object {
    return {
      'background-image': `linear-gradient(180deg, rgba(41, 51, 62, .2) 0%, rgba(41, 51, 62, .6) 80.21%, rgba(41, 51, 62, .6) 100%),
        url(${this.mobileImageSource || this.imageSource})`,
    };
  }

  public get builtLayoutCssClass(): string {
    const cssClasses = [
      'organizer-shared-page-overview',
      `organizer-shared-page-overview--${this.pageVariant}`,
    ];

    if (this.layoutCssClass) {
      cssClasses.push(this.layoutCssClass);
    }

    return cssClasses.join(' ');
  }

  public get pageVariant(): ComponentVariantEnum {
    return this.isMobileDevice
      ? ComponentVariantEnum.Light
      : ComponentVariantEnum.Dark;
  }

  private isPaymentStepDisabled(): boolean {
    return this.paramethorService.isTrue(
      ParamethorNameEnum.AllowExternalPayment
    );
  }

  protected getPagesConfig(): {
    [key: string]: OrganizerSharedPageOverviewPageInterface;
  } {
    return {
      'organizer.onboarding.profile-1': {
        stepsNumber: this.isPaymentStepDisabled() ? 3 : 4,
        activeStep: 0,
        descriptionKey: 'onboarding.profile_1.text',
        imageSource: 'assets/organizer/onboarding/profile-1/background.jpg',
        mobileImageSource: 'assets/organizer/onboarding/profile-1/mobile.jpg',
        layoutCssClass: 'organizer-onboarding-page-profile-1',
        pageTitleKey: 'seo.organizer.onboarding.profile_1.title',
        titleStepperKey: 'onboarding.profile_1.stepper.title',
      },
      'organizer.onboarding.information-1': {
        stepsNumber: this.isPaymentStepDisabled() ? 3 : 4,
        activeStep: 1,
        descriptionKey: 'onboarding.information_1.text',
        imageSource: 'assets/organizer/onboarding/information-1/background.jpg',
        layoutCssClass: 'organizer-onboarding-page-information-1',
        pageTitleKey: 'seo.organizer.onboarding.information_1.title',
        titleStepperKey: 'onboarding.information_1.stepper.title',
      },
      ...(this.isPaymentStepDisabled()
        ? {}
        : {
            'organizer.onboarding.payment-1': {
              stepsNumber: this.isPaymentStepDisabled() ? 3 : 4,
              activeStep: 2,
              descriptionKey: 'onboarding.payment_1.text',
              imageSource:
                'assets/organizer/onboarding/payment-1/background.jpg',
              mobileImageSource:
                'assets/organizer/onboarding/payment-1/mobile.jpg',
              layoutCssClass: 'organizer-onboarding-page-payment-1',
              pageTitleKey: 'seo.organizer.onboarding.payment_1.title',
              titleStepperKey: 'onboarding.payment_1.stepper.title',
            },
          }),
      'organizer.onboarding.commitment-1': {
        stepsNumber: this.isPaymentStepDisabled() ? 3 : 4,
        activeStep: this.isPaymentStepDisabled() ? 2 : 3,
        descriptionKey: 'onboarding.commitment_1.text',
        imageSource: 'assets/organizer/onboarding/commitment-1/background.jpg',
        mobileImageSource:
          'assets/organizer/onboarding/commitment-1/mobile.jpg',
        layoutCssClass: 'organizer-onboarding-page-commitment-1',
        pageTitleKey: 'seo.organizer.onboarding.commitment_1.title',
        titleStepperKey: 'onboarding.commitment_1.stepper.title',
      },
      'organizer.activity-creation.steps-1': {
        stepsNumber: 3,
        activeStep: 0,
        descriptionKey: 'activity_creation.steps_1.text',
        imageSource:
          'assets/organizer/activity-creation/steps-1/background.jpg',
        layoutCssClass: 'organizer-activity-creation-page-steps-1',
        pageTitleKey: 'seo.organizer.activity_creation.steps_1.title',
        titleStepperKey: 'activity_creation.steps_1.stepper.title',
      },
      'organizer.activity-planning.steps-2': {
        stepsNumber: 3,
        activeStep: 1,
        descriptionKey: 'activity_planning.steps_2.text',
        imageSource:
          'assets/organizer/activity-planning/steps-2/background.jpg',
        layoutCssClass: 'organizer-activity-planning-page-steps-2',
        titleStepperKey: 'activity_planning.steps_2.stepper.title',
        hasPreviousButton: false,
        nextLabelKey: 'activity_planning.steps_2.navigation_buttons.next_label',
        pageTitleKey: 'seo.organizer.activity_planning.steps_2.title',
      },
      'organizer.activity-sharing.steps-3': {
        stepsNumber: 3,
        activeStep: 2,
        descriptionKey: 'activity_sharing.steps_3.text',
        imageSource: 'assets/organizer/activity-sharing/steps-3/background.jpg',
        layoutCssClass: 'organizer-activity-sharing-page-steps-3',
        titleStepperKey: 'activity_sharing.steps_3.stepper.title',
        hasCloseButton: false,
        hasPreviousButton: false,
        nextLabelKey: 'activity_sharing.steps_3.navigation_buttons.next_label',
        pageTitleKey: 'seo.organizer.activity_sharing.steps_3.title',
      },
    };
  }

  public buildNextLabel(): void {
    if (!this.nextLabelKey) {
      return;
    }

    this.nextLabel = this.translateService.instant(this.nextLabelKey);
  }

  public setPageValues(
    pageConfig: OrganizerSharedPageOverviewPageInterface
  ): void {
    this.pageTitleKey = pageConfig.pageTitleKey;

    // Check undefined for default values.
    this.activeStep = pageConfig.activeStep;
    this.descriptionKey = pageConfig.descriptionKey;
    this.hasPreviousButton =
      pageConfig.hasPreviousButton ?? this.hasPreviousButton;
    this.hasCloseButton = pageConfig.hasCloseButton ?? this.hasCloseButton;
    this.imageSource = pageConfig.imageSource;
    this.mobileImageSource = pageConfig.mobileImageSource;
    this.nextLabelKey = pageConfig.nextLabelKey ?? this.nextLabelKey;
    this.stepsNumber = pageConfig.stepsNumber;
    this.titleStepperKey = pageConfig.titleStepperKey;
  }
}
